// TODO (MJA): We should be getting these from the API and not hardcoding them
// The only constants we should use are slugs to match up assets to models.

export const CUROLOGY_PRODUCT_NAMES = {
  customFormula: 'Custom Formula',
  spotPatches: 'Emergency spot patches',
  acneBodyWash: 'Acne body wash',
  sunscreen: 'The sunscreen',
  lipBalm: 'The lip balm',
  theMoisturizer: 'The moisturizer',
  micellarMakeupRemover: 'The micellar makeup remover',
  curologyFutureProof: 'Curology Future-Proof',
  deepCleanse: 'Deep Cleanse',
  barrierBalm: 'Barrier Balm',
  hairFormula: 'Hair formula',
  liquidPoreExfoliant: 'Liquid pore exfoliant',
  spotPatchClusters: 'Spot patch clusters',
  starterSet: 'Starter set',
  hydrationSerum: 'Hydration serum',
  bodyCleanse: 'Body Cleanse',
  emergencySpotPatchHolographic: 'Emergency Spot Patch Holographic',
} as const;

export const AGENCY_PRODUCT_NAMES = {
  futureFormula: 'Future Formula',
  darkSpotFormula: 'Dark Spot Formula',
  eyeFormula: 'Eye Formula',
  silkScreen: 'SilkScreen',
  hydroSerumEyeStick: 'HydroSerum Eye Stick',
  milkyHydrationConcentrate: 'Milky Hydration Concentrate',
  richRestorationBalm: 'Rich Restoration Balm',
  skinSmoothingBooster: 'Skin Smoothing Booster',
  cellRepair: 'Cell Repair',
  dayDefender: 'Day Defender',
  creamOilCleanser: 'Cream Oil Cleanser',
  weightlessWhippedMoisturizer: 'Whipped Moisturizer',
} as const;

export const PRODUCT_NAMES = {
  ...CUROLOGY_PRODUCT_NAMES,
  ...AGENCY_PRODUCT_NAMES,
} as const;

export type ProductKeyType = keyof typeof PRODUCT_NAMES;
export type ProductNameType = typeof PRODUCT_NAMES[ProductKeyType];

export const PRODUCT_SET_NAMES = {
  cmSet: 'Cleanser and moisturizer set',
  cloudCareDuo: 'Cloud Care Duo',
} as const;

export type ProductSetKeyType = keyof typeof PRODUCT_SET_NAMES;
export type ProductSetNameType = typeof PRODUCT_SET_NAMES[ProductSetKeyType];

export type ProductLikeKeyType = ProductKeyType | ProductSetKeyType;
export type ProductLikeNameType = ProductNameType | ProductSetNameType;

export const CUROLOGY_PRODUCT_IDS = {
  customFormula: 1,
  acneBodyWash: 4,
  spotPatches: 3,
  sunscreen: 8,
  lipBalm: 10,
  micellarMakeupRemover: 11,
  cleanser: 12,
  moisturizer: 13,
  futureProof: 16,
  deepCleanse: 17,
  silkScreen: 18,
  hairFormula: 20,
  barrierBalm: 22,
  liquidPoreExfoliant: 26,
  spotPatchClusters: 27,
  starterSet: 28,
  hydrationSerum: 29,
  bodyCleanse: 31,
  dayPrime: 37,
} as const;

export const AGENCY_PRODUCT_IDS = {
  darkSpotFormula: 6,
  futureFormula: 5,
  eyeFormula: 19,
  creamOilCleanser: 14,
  whippedMoisturizer: 15,
  silkScreen: 18,
  agencyHairFormula: 21,
  hydroSerumEyeStick: 24,
  milkyHydrationConcentrate: 25,
  richRestorationBalm: 33,
  skinSmoothingBooster: 43,
  cellRepair: 41,
  dayDefender: 44,
} as const;

export const AGENCY_REBRAND_PRODUCT_NAMES: Partial<Record<number, string>> = {
  [AGENCY_PRODUCT_IDS.futureFormula]: 'Night Renew',
  [AGENCY_PRODUCT_IDS.darkSpotFormula]: 'Dark Spot',
  [AGENCY_PRODUCT_IDS.eyeFormula]: 'Eye Revive',
  [AGENCY_PRODUCT_IDS.cellRepair]: 'Cell Repair',
  [AGENCY_PRODUCT_IDS.dayDefender]: 'Day Defender',
  [AGENCY_PRODUCT_IDS.creamOilCleanser]: 'Cleansing Balm',
  [AGENCY_PRODUCT_IDS.whippedMoisturizer]: 'Whipped Moisturizer',
};

/**
 * Rebrand statements of intent shown beneath the product name. Only products
 * the Agency relaunch designs specify one for appear here; everything else
 * keeps its existing description.
 */
export const AGENCY_REBRAND_STATEMENTS_OF_INTENT: Partial<
  Record<number, string>
> = {
  [AGENCY_PRODUCT_IDS.futureFormula]: 'The Foundation of Skin Longevity',
  [AGENCY_PRODUCT_IDS.darkSpotFormula]:
    'Precision Treatment for Hyperpigmentation',
  [AGENCY_PRODUCT_IDS.eyeFormula]:
    'Targeted Treatment for the Delicate Eye Area',
  [AGENCY_PRODUCT_IDS.dayDefender]:
    'Protection Against Environmental Stressors',
  [AGENCY_PRODUCT_IDS.cellRepair]: 'The Future of Skin Longevity',
};

export const PRODUCT_IDS = {
  ...CUROLOGY_PRODUCT_IDS,
  ...AGENCY_PRODUCT_IDS,
} as const;

export const RX_PRODUCT_IDS = [
  PRODUCT_IDS.customFormula,
  PRODUCT_IDS.futureFormula,
  PRODUCT_IDS.darkSpotFormula,
  PRODUCT_IDS.futureProof,
  PRODUCT_IDS.deepCleanse,
  PRODUCT_IDS.eyeFormula,
  PRODUCT_IDS.hairFormula,
  PRODUCT_IDS.bodyCleanse,
  PRODUCT_IDS.dayPrime,
  PRODUCT_IDS.cellRepair,
  PRODUCT_IDS.dayDefender,
];

export const CUROLOGY_PRODUCT_SET_IDS = {
  cmSet: 1,
} as const;

export const AGENCY_PRODUCT_SET_IDS = {
  cmSet: 1,
  cloudCareDuo: 2,
} as const;

export const PRODUCT_SET_IDS = {
  ...CUROLOGY_PRODUCT_SET_IDS,
  ...AGENCY_PRODUCT_SET_IDS,
} as const;

export const PRODUCT_VARIANT_IDS = {
  smallCleanser: 3,
  largeCleanser: 4,
  smallMoisturizer: 5,
  largeMoisturizer: 6,
  smallRichMoisturizer: 7,
  largeRichMoisturizer: 8,
  smallAcneCleanser: 37,
  largeAcneCleanser: 36,
  silkScreenPackette: 38,
  silkScreenLightToMedium: 39,
  silkScreenMediumToDeep: 40,
  largeHydroSerumEyeStick: 48,
  largeMilkyHydrationConcentrate: 49,
  largeLiquidPoreExfoliant: 51,
  largeSpotPatchClusters: 52,
  starterSet: 53,
  largeHydrationSerum: 54,
};

export const CUROLOGY_PRODUCT_TRIAL_SIZES = {
  acneBodyWash: '100 mL',
  cleanserAndMoisturizer: '40 mL, 25 mL',
  emergencySpotPatches: '8 pcs',
  lipBalm: '4g',
  micellarMakeupRemover: '50 mL',
};
